import { paintGlyph } from "./world";
import type { World } from "./world";
import { CELL, DEBRIS, GRAVITY, LETTER_CAP, PARTICLE_CAP } from "../shared/defs";
import { css, hex, pack } from "../shared/color";
import type { Glyph } from "../shared/types";
import type { LetterState } from "./terrain";

const BUCKET = 32;

export interface LetterBody {
  i: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  a: number;
  av: number;
  burn: number;
  life: number;
  bounces: number;
  ghost: boolean;
  sprite: HTMLCanvasElement;
  charred: HTMLCanvasElement | null;
  w: number;
  h: number;
  hw: number;
  hh: number;
  pad: number;
  color: number;
  e?: number;
  burnRate?: number;
  pinned?: boolean;
}

export class Letters {
  x: Float32Array;
  y: Float32Array;
  w: Float32Array;
  h: Float32Array;
  base: Float32Array;
  xh: Float32Array;
  alive: Uint8Array;
  popped = 0;
  bodies: LetterBody[] = [];
  private bStart: Int32Array;
  private bItems: Int32Array;
  private bx: number;
  private by: number;
  private sprites = new Map<string, { canvas: HTMLCanvasElement; w: number; h: number; pad: number; color: number }>();

  constructor(private glyphs: Glyph[], private world: World) {
    const n = glyphs.length;
    this.x = new Float32Array(n);
    this.y = new Float32Array(n);
    this.w = new Float32Array(n);
    this.h = new Float32Array(n);
    this.base = new Float32Array(n);
    this.xh = new Float32Array(n);
    this.alive = new Uint8Array(n).fill(1);
    glyphs.forEach((g, i) => {
      this.x[i] = g.x;
      this.y[i] = g.y;
      this.w[i] = g.w;
      this.h[i] = g.h;
      this.base[i] = g.base;
      this.xh[i] = Math.max(2, g.xh);
    });
    this.bx = Math.ceil(world.W / BUCKET) + 1;
    this.by = Math.ceil(world.H / BUCKET) + 1;
    const buckets = this.bx * this.by;
    const counts = new Int32Array(buckets + 1);
    const which = new Int32Array(n);
    for (let i = 0; i < n; i++) {
      which[i] = this.bucket(this.x[i] + this.w[i] / 2, this.y[i] + this.h[i] / 2);
      counts[which[i] + 1]++;
    }
    for (let i = 0; i < buckets; i++) counts[i + 1] += counts[i];
    this.bStart = counts;
    this.bItems = new Int32Array(n);
    const cursor = counts.slice();
    for (let i = 0; i < n; i++) this.bItems[cursor[which[i]]++] = i;
    for (let i = 0; i < n; i++) this.stamp(i, 1);
  }

  private bucket(x: number, y: number) {
    const cx = Math.min(this.bx - 1, Math.max(0, Math.floor(x / BUCKET)));
    const cy = Math.min(this.by - 1, Math.max(0, Math.floor(y / BUCKET)));
    return cy * this.bx + cx;
  }

  private stamp(i: number, dir: number) {
    const world = this.world;
    const x0 = Math.floor(this.x[i] / CELL);
    const x1 = Math.floor((this.x[i] + this.w[i] - 0.01) / CELL);
    const y1 = Math.floor((this.base[i] - 0.01) / CELL);
    const y0 = Math.floor((this.base[i] - this.xh[i]) / CELL);
    for (let y = Math.max(0, y0); y <= Math.min(world.GH - 1, y1); y++) {
      for (let x = Math.max(0, x0); x <= Math.min(world.GW - 1, x1); x++) {
        const cell = y * world.GW + x;
        world.lcount[cell] = Math.max(0, world.lcount[cell] + dir);
      }
    }
  }

  query(x0: number, y0: number, x1: number, y1: number, fn: (i: number) => void) {
    const c0 = Math.max(0, Math.floor((x0 - 24) / BUCKET));
    const c1 = Math.min(this.bx - 1, Math.floor((x1 + 24) / BUCKET));
    const r0 = Math.max(0, Math.floor((y0 - 24) / BUCKET));
    const r1 = Math.min(this.by - 1, Math.floor((y1 + 24) / BUCKET));
    for (let y = r0; y <= r1; y++) {
      for (let x = c0; x <= c1; x++) {
        const b = y * this.bx + x;
        for (let k = this.bStart[b]; k < this.bStart[b + 1]; k++) {
          const i = this.bItems[k];
          if (!this.alive[i]) continue;
          if (this.x[i] + this.w[i] < x0 || this.x[i] > x1 || this.y[i] + this.h[i] < y0 || this.y[i] > y1) continue;
          fn(i);
        }
      }
    }
  }

  private sprite(i: number) {
    const g = this.glyphs[i];
    const key = `${g.font}|${g.color}|${g.ch}|${g.underline ? 1 : 0}|${g.w.toFixed(1)}`;
    let hit = this.sprites.get(key);
    if (!hit) {
      const z = this.world.Z;
      const pad = Math.ceil(g.size * 0.35);
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.ceil((g.w + pad * 2) * z));
      canvas.height = Math.max(1, Math.ceil((g.h + pad * 2) * z));
      const ctx = canvas.getContext("2d")!;
      paintGlyph(ctx, g, z, g.x - pad, g.y - pad);
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      hit = { canvas, w: g.w, h: g.h, pad, color: g.color };
      this.sprites.set(key, hit);
    }
    return hit;
  }

  private clearText(i: number) {
    const z = this.world.Z;
    const x0 = Math.round(this.x[i] * z);
    const x1 = Math.round((this.x[i] + this.w[i]) * z);
    const y0 = Math.floor(this.y[i] * z) - 1;
    const y1 = Math.ceil((this.y[i] + this.h[i]) * z) + 1;
    this.world.tx.clearRect(x0, y0, x1 - x0, y1 - y0);
  }

  hitBox(i: number): [number, number, number, number] {
    return [this.x[i], this.y[i], this.x[i] + this.w[i], this.y[i] + this.h[i]];
  }

  /** A glyph sitting on the reserved exit stays in the denominator until the portal opens. */
  private reserved(i: number): boolean {
    const k = this.world.keep;
    if (!k) return false;
    return this.x[i] < k.x + k.w && this.x[i] + this.w[i] > k.x && this.y[i] < k.y + k.h && this.y[i] + this.h[i] > k.y;
  }

  /** True when a living glyph overlaps a page-space box. */
  covers(rect: { x: number; y: number; w: number; h: number }): boolean {
    const n = this.alive.length;
    for (let i = 0; i < n; i++) {
      if (!this.alive[i]) continue;
      if (this.x[i] < rect.x + rect.w && this.x[i] + this.w[i] > rect.x && this.y[i] < rect.y + rect.h && this.y[i] + this.h[i] > rect.y) return true;
    }
    return false;
  }

  remove(i: number) {
    if (!this.alive[i] || this.reserved(i)) return;
    this.world.log?.letter(i, null);
    this.alive[i] = 0;
    this.stamp(i, -1);
    this.popped++;
    this.clearText(i);
  }

  detach(i: number, vx: number, vy: number, burn = -1, ghost = false, motion?: LetterState | null): LetterBody | null {
    if (!this.alive[i] || this.reserved(i)) return null;
    if (motion === null) {
      this.remove(i);
      return null;
    }
    this.alive[i] = 0;
    this.stamp(i, -1);
    this.popped++;
    this.clearText(i);
    const av = motion?.av ?? (Math.random() - 0.5) * 8;
    const heat = motion?.burn ?? burn;
    if (!ghost) this.world.log?.letter(i, { vx, vy, av, burn: heat, pinned: motion?.pinned ?? false });
    const spr = this.sprite(i);
    if (this.bodies.length >= LETTER_CAP) {
      const old = this.bodies.shift();
      if (old && !old.ghost) this.settle(old);
    }
    const body: LetterBody = {
      i, x: this.x[i] + this.w[i] / 2, y: this.y[i] + this.h[i] / 2, vx, vy, a: 0, av,
      burn: heat, life: heat > 0 ? 0.4 + (motion ? 0.4 : Math.random() * 0.5) : 8, bounces: 0, ghost, sprite: spr.canvas,
      charred: null, w: spr.w, h: spr.h, hw: Math.max(1.5, spr.w / 2), hh: Math.max(2, spr.h * 0.34), pad: spr.pad, color: spr.color,
      pinned: motion?.pinned ?? false,
    };
    this.bodies.push(body);
    return body;
  }

  private settle(body: LetterBody) {
    const world = this.world;
    const z = world.Z;
    const color = body.burn > 0.4 ? pack(24, 12, 8) : body.color;
    const pad = body.pad;
    world.pg.save();
    const cos = Math.cos(body.a) * z;
    const sin = Math.sin(body.a) * z;
    world.pg.setTransform(cos, sin, -sin, cos, body.x * z, body.y * z);
    world.pg.drawImage(body.sprite, -body.w / 2 - pad, -body.h / 2 - pad, body.w + pad * 2, body.h + pad * 2);
    world.pg.restore();
    const flat = Math.abs(Math.sin(body.a));
    const up = Math.abs(Math.cos(body.a));
    const halfW = (body.hw * up + body.hh * flat) * 0.8;
    const halfH = body.hw * flat + body.hh * up;
    for (let y = Math.floor((body.y - halfH * 0.7) / CELL); y <= Math.floor((body.y + halfH - 0.5) / CELL); y++) {
      for (let x = Math.floor((body.x - halfW) / CELL); x <= Math.floor((body.x + halfW) / CELL); x++) world.markDebris(x, y, color);
    }
  }

  /** Pull a glyph off the page and set it alight. `pinned` letters stay put and spread. */
  ignite(i: number, energy: number, pinned = false): LetterBody | null {
    const body = this.detach(i, 0, 0, 0, false, { vx: 0, vy: 0, av: 0, burn: 0, pinned });
    if (!body) return null;
    body.burn = 0;
    body.e = pinned ? 0 : energy;
    body.burnRate = 0.38 + Math.random() * 0.22;
    body.pinned = pinned;
    return body;
  }

  step(dt: number) {
    const world = this.world;
    for (let n = this.bodies.length - 1; n >= 0; n--) {
      const b = this.bodies[n];
      if (b.burn >= 0) {
        b.burn += dt * (b.burnRate ?? (0.7 + (b.i % 7) * 0.1));
        if (b.burnRate && b.burn >= 1) {
          this.bodies.splice(n, 1);
          continue;
        }
        b.life -= dt;
      }
      if (b.pinned && Math.abs(b.vx) < 1 && Math.abs(b.vy) < 1 && Math.abs(b.av) < 0.5) {
        if ((b.e ?? 0) > 0 && b.burn > 0.12 && Math.random() < 3.2 * dt) {
          const near: number[] = [];
          const reachX = b.hw * 2.6 + 4;
          const reachY = b.hh * 1.6 + 3;
          this.query(b.x - reachX, b.y - reachY, b.x + reachX, b.y + reachY, (index) => near.push(index));
          if (near.length && this.ignite(near[Math.floor(Math.random() * near.length)], (b.e ?? 1) - 1)) b.e = (b.e ?? 0) - 0.5;
        }
        continue;
      }
      b.vy += GRAVITY * 0.82 * dt;
      b.vx *= Math.exp(-0.35 * dt);
      b.av *= Math.exp(-0.4 * dt);
      b.x += b.vx * dt;
      b.y += b.vy * dt;
      b.a += b.av * dt;
      if (b.y > world.H + 200 || b.life < 0) {
        if (!b.ghost && b.life < 0) this.settle(b);
        this.bodies.splice(n, 1);
        continue;
      }
      if (b.vy > 0 && b.bounces < 4) {
        const foot = b.y + b.hh;
        const ground = world.findSurface(b.x, b.x + 1, foot - 2, foot);
        if (!Number.isNaN(ground) && foot >= ground) {
          b.y = ground - b.hh;
          if (Math.abs(b.vy) > 140) {
            b.vy *= -(0.24 + Math.random() * 0.1);
            b.vx *= 0.7;
            b.bounces++;
          } else {
            b.vy = 0;
            b.vx *= 0.8;
            if (Math.abs(b.vx) < 8 && b.burn < 0) {
              if (!b.ghost) this.settle(b);
              this.bodies.splice(n, 1);
            }
          }
        }
      }
    }
  }

  draw(ctx: CanvasRenderingContext2D, camX: number, camY: number, scale: number) {
    for (const b of this.bodies) {
      ctx.save();
      ctx.translate((b.x - camX) * scale, (b.y - camY) * scale);
      ctx.rotate(b.a);
      const dx = (-b.w / 2 - b.pad) * scale;
      const dy = (-b.h / 2 - b.pad) * scale;
      const dw = (b.w + b.pad * 2) * scale;
      const dh = (b.h + b.pad * 2) * scale;
      if (b.burn < 0) ctx.drawImage(b.sprite, dx, dy, dw, dh);
      else {
        const blend = Math.min(1, b.burn * 1.6);
        ctx.globalAlpha = 1 - blend;
        ctx.drawImage(b.sprite, dx, dy, dw, dh);
        ctx.globalAlpha = blend;
        ctx.drawImage(this.char(b), dx, dy, dw, dh);
      }
      ctx.restore();
    }
    ctx.globalAlpha = 1;
  }

  private char(b: LetterBody) {
    if (!b.charred) {
      const c = document.createElement("canvas");
      c.width = b.sprite.width;
      c.height = b.sprite.height;
      const g = c.getContext("2d")!;
      g.drawImage(b.sprite, 0, 0);
      g.globalCompositeOperation = "source-atop";
      g.fillStyle = "rgba(24,12,8,0.9)";
      g.fillRect(0, 0, c.width, c.height);
      b.charred = c;
    }
    return b.charred;
  }
}

export interface Chunk {
  x: number;
  y: number;
  vx: number;
  vy: number;
  a: number;
  av: number;
  w: number;
  h: number;
  canvas: HTMLCanvasElement;
  rest: number;
  age: number;
  ghost: boolean;
}

export class Chunks {
  list: Chunk[] = [];
  broken = 0;

  add(canvas: HTMLCanvasElement, x: number, y: number, w: number, h: number, vx: number, vy: number, av = (Math.random() - 0.5) * 3, ghost = false) {
    this.list.push({
      x: x + w / 2, y: y + h / 2, vx, vy, a: 0, av,
      w, h, canvas, rest: 0, age: 0, ghost,
    });
    this.broken++;
  }

  step(dt: number, world: World) {
    for (let i = this.list.length - 1; i >= 0; i--) {
      const c = this.list[i];
      c.age += dt;
      c.vy += GRAVITY * dt;
      c.vx *= Math.exp(-0.08 * dt);
      c.av *= Math.exp(-0.15 * dt);
      c.x += c.vx * dt;
      c.y += c.vy * dt;
      c.a += c.av * dt;
      const ground = world.findSurface(c.x - c.w / 2, c.x + c.w / 2, c.y - 4, c.y + c.h / 2);
      if (!Number.isNaN(ground) && c.y + c.h / 2 >= ground && c.vy > 0) {
        const hit = c.vy;
        c.y = ground - c.h / 2;
        const bounce = hit > 250 ? 0.18 : 0;
        c.vy = -hit * bounce;
        c.vx *= 0.72;
        c.av *= 0.5;
        const still = Math.abs(c.vx) < 10 && Math.abs(c.vy) < 14 && Math.abs(c.av) < 0.25;
        c.rest = still ? c.rest + dt : 0;
      } else c.rest = 0;
      if (c.y > world.H + 400) {
        this.list.splice(i, 1);
        continue;
      }
      if (c.rest > 0.45 || c.age > 14) {
        if (!c.ghost) this.bake(c, world);
        this.list.splice(i, 1);
      }
    }
  }

  /** A bullet struck a flying chunk. The chunk takes the shove; the page is unchanged until it bakes. */
  strike(x: number, y: number, vx: number, vy: number): boolean {
    for (const c of this.list) {
      if (Math.abs(x - c.x) > c.w / 2 + 2 || Math.abs(y - c.y) > c.h / 2 + 2) continue;
      c.vx += Math.max(-200, Math.min(200, vx * 0.04));
      c.vy += Math.max(-200, Math.min(80, vy * 0.04)) - 30;
      c.av += (Math.random() - 0.5) * 2;
      c.rest = 0;
      return true;
    }
    return false;
  }

  private bake(c: Chunk, world: World) {
    const reach = Math.hypot(c.w, c.h) / 2;
    for (let y = Math.floor((c.y - reach) / CELL); y <= Math.ceil((c.y + reach) / CELL); y++) {
      for (let x = Math.floor((c.x - reach) / CELL); x <= Math.ceil((c.x + reach) / CELL); x++) {
        const dx = (x + 0.5) * CELL - c.x;
        const dy = (y + 0.5) * CELL - c.y;
        if (dx * dx + dy * dy > reach * reach) continue;
        const color = world.col[Math.min(world.col.length - 1, Math.max(0, y * world.GW + x))] || pack(80, 70, 60);
        world.bakeDebris(x, y, color);
      }
    }
  }

  draw(ctx: CanvasRenderingContext2D, camX: number, camY: number, scale: number) {
    for (const c of this.list) {
      ctx.save();
      ctx.translate((c.x - camX) * scale, (c.y - camY) * scale);
      ctx.rotate(c.a);
      ctx.drawImage(c.canvas, (-c.w / 2) * scale, (-c.h / 2) * scale, c.w * scale, c.h * scale);
      ctx.restore();
    }
  }
}

export interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  size: number;
  color: string;
  grav: number;
  rubble: boolean;
  glow: boolean;
  kind: "spark" | "smoke" | "flame" | "crumb" | "fireball" | "chunk" | "ember" | "ion";
  seed?: number;
  /** 1 leaves smoke when it dies. 2 leaves the larger ember-flagged puff. */
  puff?: 0 | 1 | 2;
  /** Drawn into the cell buffer as a streak instead of a canvas arc. */
  buf?: "fire" | "ion";
  /** Set once a flame pellet is inside solid terrain, which dumps more heat. */
  touch?: boolean;
}

const HOT = ["#fff9e6", "#ffe26b", "#ff8a2a", "#f2561d", "#5b5566", "#2a2633"];
const SMOKE_RAMP = ["#7b6f72", "#5d5660", "#433d48", "#2b2630", "#1a171e"];

const RGB_CACHE = new Map<string, [number, number, number]>();
function rgbOf(color: string): [number, number, number] {
  let rgb = RGB_CACHE.get(color);
  if (rgb) return rgb;
  const hex = color.replace("#", "");
  rgb = hex.length < 6 ? [91, 85, 102] : [parseInt(hex.slice(0, 2), 16), parseInt(hex.slice(2, 4), 16), parseInt(hex.slice(4, 6), 16)];
  if (RGB_CACHE.size < 512) RGB_CACHE.set(color, rgb);
  return rgb;
}

function rampAt(t: number): [number, number, number] {
  const x = Math.max(0, Math.min(0.9999, t)) * (SMOKE_RAMP.length - 1);
  const i = Math.floor(x);
  const f = x - i;
  const a = rgbOf(SMOKE_RAMP[i]);
  const b = rgbOf(SMOKE_RAMP[Math.min(SMOKE_RAMP.length - 1, i + 1)]);
  return [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f, a[2] + (b[2] - a[2]) * f];
}

function smokeRgb(base: string, age: number, out: number[]) {
  const t = Math.max(0, Math.min(0.9999, age));
  const ramp = rampAt(base ? t : t * 1.05);
  const src = base ? rgbOf(base) : ramp;
  const mix = base ? t : 1;
  out[0] = (src[0] + (ramp[0] - src[0]) * mix) | 0;
  out[1] = (src[1] + (ramp[1] - src[1]) * mix) | 0;
  out[2] = (src[2] + (ramp[2] - src[2]) * mix) | 0;
}

/**
 * Smoke is hundreds of overlapping translucent pixel discs. Painting each one on the
 * full-resolution canvas was the main cost of a big fight on phones, so the discs are
 * blended here at one pixel per cell and the layer goes on screen in a single draw.
 * Same cells, same colours and alpha (source-over is associative), one draw call.
 */
class SmokeLayer {
  canvas: HTMLCanvasElement | null = null;
  g: CanvasRenderingContext2D | null = null;
  image: ImageData | null = null;
  acc = new Float32Array(0);
  w = 0;
  h = 0;
  ox = 0;
  oy = 0;
  used = false;

  begin(cols: number, rows: number, ox: number, oy: number) {
    if (cols !== this.w || rows !== this.h || !this.canvas) {
      this.w = cols;
      this.h = rows;
      this.acc = new Float32Array(cols * rows * 4);
      this.canvas = document.createElement("canvas");
      this.canvas.width = cols;
      this.canvas.height = rows;
      this.g = this.canvas.getContext("2d", { willReadFrequently: true });
      this.image = this.g ? this.g.createImageData(cols, rows) : null;
    } else if (this.used) this.acc.fill(0);
    this.ox = ox;
    this.oy = oy;
    this.used = false;
  }

  private cell(x: number, y: number, r: number, g: number, b: number, a: number) {
    x -= this.ox;
    y -= this.oy;
    if (x < 0 || y < 0 || x >= this.w || y >= this.h) return;
    const i = (y * this.w + x) * 4;
    const acc = this.acc;
    const k = 1 - a;
    acc[i] = r * a + acc[i] * k;
    acc[i + 1] = g * a + acc[i + 1] * k;
    acc[i + 2] = b * a + acc[i + 2] * k;
    acc[i + 3] = a + acc[i + 3] * k;
  }

  disc(gx: number, gy: number, radius: number, r: number, g: number, b: number, a: number) {
    if (a <= 0) return;
    if (a > 1) a = 1;
    this.used = true;
    if (radius <= 0.5) {
      this.cell(Math.round(gx), Math.round(gy), r, g, b, a);
      return;
    }
    const y0 = Math.floor(gy - radius);
    const y1 = Math.ceil(gy + radius);
    const r2 = radius * radius;
    const base = gx - 0.5;
    for (let y = y0; y <= y1; y++) {
      const dy = y + 0.5 - gy;
      const rem = r2 - dy * dy;
      if (rem < 0) continue;
      const half = Math.sqrt(rem);
      let xa = Math.ceil(base - half);
      let xb = Math.floor(base + half);
      while (xa <= xb && (xa + 0.5 - gx) ** 2 + dy * dy > r2) xa++;
      while (xb >= xa && (xb + 0.5 - gx) ** 2 + dy * dy > r2) xb--;
      for (let x = xa; x <= xb; x++) this.cell(x, y, r, g, b, a);
    }
  }

  flush(ctx: CanvasRenderingContext2D, camX: number, camY: number, scale: number) {
    if (!this.used || !this.g || !this.image || !this.canvas) return;
    const acc = this.acc;
    const px = this.image.data;
    for (let i = 0; i < acc.length; i += 4) {
      const a = acc[i + 3];
      if (a <= 0) {
        px[i + 3] = 0;
        continue;
      }
      const inv = 1 / a;
      px[i] = acc[i] * inv;
      px[i + 1] = acc[i + 1] * inv;
      px[i + 2] = acc[i + 2] * inv;
      px[i + 3] = a * 255 + 0.5;
    }
    this.g.putImageData(this.image, 0, 0);
    ctx.save();
    ctx.imageSmoothingEnabled = false;
    ctx.globalAlpha = 1;
    ctx.drawImage(this.canvas, (this.ox * CELL - camX) * scale, (this.oy * CELL - camY) * scale, this.w * CELL * scale, this.h * CELL * scale);
    ctx.restore();
  }
}
const RGB = [0, 0, 0];

function stampDisc(ctx: CanvasRenderingContext2D, gx: number, gy: number, radius: number, camX: number, camY: number, scale: number) {
  if (radius <= 0.5) {
    const cx = Math.round(gx);
    const cy = Math.round(gy);
    ctx.fillRect((cx * CELL - camX) * scale, (cy * CELL - camY) * scale, CELL * scale, CELL * scale);
    return;
  }
  // One path per disc, one rect per row span: the same cells as a fillRect per cell,
  // but a big smoke puff is ~10 rects in a single fill instead of ~80 separate draws.
  const pix = CELL * scale;
  const y0 = Math.floor(gy - radius);
  const y1 = Math.ceil(gy + radius);
  const r2 = radius * radius;
  const base = gx - 0.5;
  ctx.beginPath();
  for (let y = y0; y <= y1; y++) {
    const dy = y + 0.5 - gy;
    const rem = r2 - dy * dy;
    if (rem < 0) continue;
    const half = Math.sqrt(rem);
    // Cells whose centre (x + 0.5) lies within the circle: dx*dx <= r2 - dy*dy.
    let xa = Math.ceil(base - half);
    let xb = Math.floor(base + half);
    while (xa <= xb && (xa + 0.5 - gx) ** 2 + dy * dy > r2) xa++;
    while (xb >= xa && (xb + 0.5 - gx) ** 2 + dy * dy > r2) xb--;
    if (xa > xb) continue;
    ctx.rect((xa * CELL - camX) * scale, (y * CELL - camY) * scale, (xb - xa + 1) * pix, pix);
  }
  ctx.fill();
}

export class Particles {
  list: Particle[] = [];
  cap: number;
  t = 0;

  constructor(scale = 1) {
    this.cap = Math.max(1000, Math.round(PARTICLE_CAP * Math.min(1, scale)));
  }

  private stepping = false;

  spawn(p: Particle) {
    if (p.seed == null) p.seed = Math.random() * 100;
    this.list.push(p);
    // Oldest-first culling, in batches: one splice per few hundred spawns instead of one per spawn.
    if (!this.stepping && this.list.length > this.cap + (this.cap >> 5)) this.trim();
  }

  private trim() {
    if (this.list.length > this.cap) this.list.splice(0, this.list.length - this.cap);
  }

  burst(x: number, y: number, color: number, n: number, speed: number, rubble = false) {
    const cssColor = css(color);
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const s = speed * (0.3 + Math.random());
      this.spawn({
        x, y, vx: Math.cos(a) * s, vy: Math.sin(a) * s - Math.random() * speed * 0.3,
        life: 0.35 + Math.random() * 0.7, max: 1, size: 1 + Math.random() * 2.2,
        color: rubble ? cssColor : HOT[Math.floor(Math.random() * HOT.length)],
        grav: rubble ? GRAVITY * 0.35 : 200, rubble, glow: !rubble, kind: rubble ? "crumb" : "spark",
      });
    }
  }

  smoke(x: number, y: number, vx: number, vy: number, size: number, life: number, color?: string) {
    const grey = color === undefined ? (Math.random() < 0.5 ? "#5b5566" : "#3f3a4a") : color;
    this.spawn({
      x, y, vx, vy, life, max: life, size, color: grey, grav: -18, rubble: false, glow: false, kind: "smoke",
    });
  }

  chip(x: number, y: number, vx: number, vy: number, color: number, life = 0.9, size = 2) {
    this.spawn({
      x, y, vx, vy, life, max: life, size,
      color: css(color), grav: GRAVITY, rubble: true, glow: false, kind: "crumb",
    });
  }

  flamePuff(x: number, y: number, vx: number, vy: number, size: number, life: number) {
    this.spawn({
      x, y, vx, vy, life, max: life, size, color: HOT[Math.floor(Math.random() * 4)], grav: -40, rubble: false, glow: true, kind: "flame",
    });
  }

  /** Push nearby particles. Fireballs are left alone, smoke only takes a third. */
  impulse(x: number, y: number, radius: number, power: number) {
    const r2 = radius * radius;
    for (const p of this.list) {
      if (p.kind === "fireball") continue;
      const dx = p.x - x;
      const dy = p.y - y;
      const d2 = dx * dx + dy * dy;
      if (d2 > r2 || d2 <= 0) continue;
      const dist = Math.sqrt(d2);
      const scale = p.kind === "smoke" || p.kind === "crumb" ? 0.35 : 1;
      const push = power * (1 - dist / radius) * scale;
      p.vx += (dx / dist) * push;
      p.vy += (dy / dist) * push - push * 0.3;
    }
  }

  flame(x: number, y: number, angle: number, n: number, speed: number, burst = false, inheritVx = 0, inheritVy = 0) {
    for (let i = 0; i < n; i++) {
      const a = angle + (Math.random() * 2 - 1) * (burst ? 0.2 : 0.1);
      const s = speed * (0.78 + Math.random() * 0.37) * (burst ? 0.6 : 1);
      const life = 0.42 + Math.random() * 0.3;
      const vx = Math.cos(a) * s + inheritVx * 0.4;
      const vy = Math.sin(a) * s + inheritVy * 0.3;
      const back = Math.random() * (1 / 60);
      this.spawn({
        x: x - vx * back, y: y - vy * back, vx, vy, life, max: life,
        size: (1.3 + Math.random() * 0.9) * (burst ? 1.4 : 1) * CELL,
        color: HOT[Math.floor(Math.random() * 4)], grav: 80, rubble: false, glow: true, kind: "flame",
        puff: Math.random() < 0.45 ? 1 : 0,
      });
    }
  }

  step(dt: number, world: World, fire?: Fire) {
    this.stepping = true;
    try {
      this.stepAll(dt, world, fire);
    } finally {
      this.stepping = false;
    }
    // Drop the dead in one pass, keeping draw order. Particles spawned during the step are alive.
    const list = this.list;
    let j = 0;
    for (let i = 0; i < list.length; i++) if (list[i].life > 0) list[j++] = list[i];
    list.length = j;
    this.trim();
  }

  private stepAll(dt: number, world: World, fire?: Fire) {
    this.t += dt;
    const frame = this.t * 60 | 0;
    for (let i = this.list.length - 1; i >= 0; i--) {
      const p = this.list[i];
      p.life -= dt;
      if (p.life <= 0) {
        if ((p.kind === "flame" || p.kind === "fireball") && p.puff) {
          const big = p.puff === 2;
          const life = big ? 1.3 + Math.random() : 0.8 + Math.random() * 0.6;
          this.smoke(p.x, p.y, p.vx * 0.6, p.vy * 0.6 - 12, Math.max(CELL, p.size * (big ? 0.85 : 0.7)), life, p.kind === "flame" ? "#3a2e28" : undefined);
        }
        if (p.kind === "chunk") this.smoke(p.x, p.y, (Math.random() - 0.5) * 30, -20, CELL * 1.4, 0.35);
        continue;
      }
      if (p.kind === "smoke") {
        p.vx += Math.sin(this.t * 1.3 + (p.seed ?? 0)) * 16 * dt;
        p.vx -= p.vx * 1.6 * dt;
        p.vy -= 32 * dt;
        p.vy -= p.vy * 1.2 * dt;
      } else if (p.kind === "flame") {
        const age = 1 - p.life / p.max;
        const drag = (2.4 + age * 2) * dt;
        p.vx -= p.vx * drag;
        p.vy -= p.vy * drag;
        p.vy -= (80 + age * 420) * dt;
        p.vx += Math.sin(this.t * 19 + (p.seed ?? 0)) * 90 * age * dt;
        const nx = p.x + p.vx * dt;
        const ny = p.y + p.vy * dt;
        const col = Math.floor(nx / CELL);
        const row = Math.floor(ny / CELL);
        const inside = col >= 0 && row >= 0 && col < world.GW && row < world.GH;
        const mat = inside ? world.mat[row * world.GW + col] : 0;
        if (row >= world.GH || mat === DEBRIS) {
          const speed = Math.hypot(p.vx, p.vy);
          const open = !world.solid(col, row - 1);
          p.vx = p.vx * -0.15 + (Math.random() * 2 - 1) * speed * 0.35;
          p.vy = open ? -Math.abs(p.vy) * 0.2 - 40 : p.vy * -0.25;
          p.touch = true;
          if (p.life > 0.35) p.life = Math.max(p.life * 0.8, 0.2);
        } else {
          if (inside && world.solid(col, row)) {
            p.vx -= p.vx * 5.5 * dt;
            p.vy -= p.vy * 5.5 * dt;
            p.touch = true;
          }
          p.x = nx;
          p.y = ny;
        }
      } else if (p.kind === "fireball") {
        p.vx += Math.sin(this.t * 17 + (p.seed ?? 0)) * 60 * dt;
        p.vx -= p.vx * 5 * dt;
        p.vy -= 190 * dt;
        p.vy -= p.vy * 3.2 * dt;
      } else if (p.kind === "chunk") {
        p.vy += GRAVITY * 0.8 * dt;
        p.vx -= p.vx * 0.9 * dt;
        if (Math.random() < 0.9 && this.list.length < this.cap - 8) {
          const life = 0.16 + Math.random() * 0.16;
          const fade = 0.5 + (p.life / p.max) * 0.6;
          this.spawn({
            x: p.x, y: p.y,
            vx: p.vx * 0.1 + (Math.random() - 0.5) * 30,
            vy: p.vy * 0.1 - 20,
            life, max: life,
            size: (0.9 + Math.random() * 0.8) * fade * CELL,
            color: "#ff8a2a", grav: -190, rubble: false, glow: true, kind: "fireball",
            puff: Math.random() < 0.4 ? 1 : 0,
          });
        }
      } else if (p.kind === "ember") {
        p.vy += (-60 + Math.sin(this.t * 3 + (p.seed ?? 0)) * 40) * dt;
        p.vx += Math.cos(this.t * 2.3 + (p.seed ?? 0)) * 90 * dt;
        p.vx -= p.vx * 1.6 * dt;
        p.vy -= p.vy * 1.6 * dt;
      } else if (p.buf || p.kind === "ion") {
        p.vy += GRAVITY * 0.45 * dt;
        p.vx -= p.vx * 2.2 * dt;
        p.vy -= p.vy * 2.2 * dt;
      } else p.vy += p.grav * dt;
      if (p.kind !== "flame") {
        p.x += p.vx * dt;
        p.y += p.vy * dt;
      }
      if (p.kind === "flame" && fire) {
        const age = 1 - p.life / Math.max(1e-4, p.max);
        if (age >= 0.02 && ((i + frame) & 1) === 0) fire.heatAt(p.x, p.y, p.touch ? 0.5 : 0.17 * Math.max(0, 1.2 - age));
      }
    }
  }

  private smokeLayer = new SmokeLayer();

  draw(ctx: CanvasRenderingContext2D, camX: number, camY: number, scale: number) {
    const layer = this.smokeLayer;
    const cols = Math.ceil(ctx.canvas.width / scale / CELL) + 3;
    const rows = Math.ceil(ctx.canvas.height / scale / CELL) + 3;
    layer.begin(cols, rows, Math.floor(camX / CELL) - 1, Math.floor(camY / CELL) - 1);
    for (const p of this.list) {
      if (p.kind !== "smoke") continue;
      const age = 1 - p.life / p.max;
      const cells = p.size / CELL;
      const puff = cells * (0.65 + Math.min(1, age * 2.5) * 0.45 + age * 0.4);
      const cover = Math.min(1, age * 12) * Math.min(1, (p.life / p.max) * 2.4) * 0.58;
      const gx = p.x / CELL;
      const gy = p.y / CELL;
      smokeRgb(p.color, age, RGB);
      layer.disc(gx, gy, puff, RGB[0], RGB[1], RGB[2], cover * 0.45);
      layer.disc(gx + puff * 0.05, gy + puff * 0.06, puff * 0.74, RGB[0], RGB[1], RGB[2], cover * 0.62);
      if (age < 0.5) layer.disc(gx - puff * 0.22, gy - puff * 0.3, puff * 0.5, 255, 255, 255, cover * 0.4 * (1 - age * 2));
    }
    layer.flush(ctx, camX, camY, scale);
    ctx.save();
    for (const p of this.list) {
      if (p.kind === "smoke") continue;
      const age = 1 - p.life / p.max;
      const alpha = Math.max(0, p.life / p.max);
      ctx.globalAlpha = alpha;
      ctx.fillStyle = p.color;
      const x = (p.x - camX) * scale;
      const y = (p.y - camY) * scale;
      if (p.kind === "crumb") {
        const r = Math.max(scale, p.size * (0.7 + age * 0.4) * scale);
        ctx.fillRect(x, y, r, r);
      } else if (p.kind === "fireball" || p.kind === "chunk" || p.kind === "flame" || p.kind === "ember" || p.kind === "ion" || p.buf) {
        continue;
      } else if (p.kind === "spark") {
        const sp = Math.hypot(p.vx, p.vy) || 1;
        const len = Math.min(9, 1 + sp * 0.011);
        const tx = p.x - (p.vx / sp) * len;
        const ty = p.y - (p.vy / sp) * len;
        ctx.globalAlpha = Math.min(1, alpha * 1.4);
        ctx.strokeStyle = age < 0.4 ? "#fff3a6" : age < 0.7 ? "#ffe26b" : "#ff8a2a";
        ctx.lineWidth = Math.max(1, scale);
        ctx.beginPath();
        ctx.moveTo((tx - camX) * scale, (ty - camY) * scale);
        ctx.lineTo(x, y);
        ctx.stroke();
      } else if (p.kind === "chunk") {
        const flick = Math.sin(p.life * 40 + p.x) > 0;
        const gx = p.x / CELL;
        const gy = p.y / CELL;
        ctx.globalAlpha = 1;
        ctx.fillStyle = "#f2561d";
        stampDisc(ctx, gx, gy, 1.6, camX, camY, scale);
        ctx.fillStyle = flick ? "#fff3a6" : "#ffe26b";
        ctx.fillRect(x, y, 2 * scale, 2 * scale);
        ctx.fillRect(x + 2 * scale, y, 2 * scale, 2 * scale);
      } else if (p.kind === "fireball") {
        const cells = p.size / CELL;
        const rise = Math.min(1, age * 7);
        const puff = cells * (0.72 + 0.28 * rise) * (age < 0.72 ? 1 : Math.max(0.45, 1 - (age - 0.72) * 1.4));
        const gx = p.x / CELL;
        const gy = p.y / CELL;
        const shell = age < 0.78 ? "#ff8a2a" : age < 0.9 ? "#f2561d" : "#8a1f17";
        const hot = age < 0.18 ? "#fffbe8" : age < 0.4 ? "#ffe26b" : age < 0.62 ? "#ffc23d" : "#ff8a2a";
        ctx.globalAlpha = age > 0.9 ? Math.min(1, (p.life / p.max) * 4) : 1;
        ctx.fillStyle = shell;
        stampDisc(ctx, gx, gy, puff, camX, camY, scale);
        if (age < 0.9) {
          ctx.globalAlpha = 1;
          ctx.fillStyle = hot;
          stampDisc(ctx, gx - puff * 0.12, gy - puff * 0.16, puff * 0.72, camX, camY, scale);
          if (age < 0.45 && puff > 2) {
            ctx.fillStyle = "#fff3a6";
            stampDisc(ctx, gx - puff * 0.22, gy - puff * 0.28, puff * 0.34, camX, camY, scale);
          }
        }
      } else {
        const cells = p.size / CELL;
        const puff = cells * (0.55 + Math.pow(age, 0.6) * 1.9);
        const r = Math.max(2, puff * scale * CELL);
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = alpha * 0.9;
        ctx.beginPath();
        ctx.arc(x - r * 0.14, y - r * 0.2, r * 0.76, 0, Math.PI * 2);
        ctx.fill();
        if (r > 6) {
          ctx.globalAlpha = alpha * 0.55;
          ctx.beginPath();
          ctx.arc(x, y - r * 0.7, r * 0.4, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }
    ctx.restore();
  }
}

interface Flame {
  fuel: number;
  max: number;
  e: number;
  local: boolean;
}

const FIRE_N: Array<[number, number]> = [[-1, -1], [0, -1], [1, -1], [-1, 0], [1, 0], [-1, 1], [0, 1], [1, 1]];

export class Fire {
  cells = new Map<number, Flame>();
  /** Block indexes whose cells burned away since the last flush. */
  burned: number[] = [];
  private heat = new Map<number, number>();

  private energy(world: World, cell: number): number {
    const mat = world.mat[cell];
    if (!mat) return 0;
    if (mat === 3) return 3;
    if (mat === 1) return 8;
    const owner = world.owner[cell] ?? -1;
    const block = owner >= 0 ? world.blocks[owner] : undefined;
    if (block?.kind === "image") return 8;
    if (mat === 2) return 6;
    return 0;
  }

  ignite(world: World, x: number, y: number) {
    const cx = Math.floor(x / CELL);
    const cy = Math.floor(y / CELL);
    if (cx < 0 || cy < 0 || cx >= world.GW || cy >= world.GH) return;
    this.light(world, cy * world.GW + cx, this.energy(world, cy * world.GW + cx));
  }

  /** Flame volume heats a 3x3. A cell ignites once the heat reaches 1. */
  heatAt(x: number, y: number, amount: number) {
    const cx = Math.floor(x / CELL);
    const cy = Math.floor(y / CELL);
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        const col = cx + dx;
        const row = cy + dy;
        if (col < 0 || row < 0 || col >= this.boundW || row >= this.boundH) continue;
        const cell = row * this.boundW + col;
        if (this.boundMat[cell] === 0 || this.cells.has(cell)) continue;
        const gain = amount * (dx === 0 && dy === 0 ? 1 : 0.45);
        const hot = (this.heat.get(cell) ?? 0) + gain;
        if (hot >= 1) this.light(this.boundWorld, cell, this.energy(this.boundWorld, cell));
        else this.heat.set(cell, hot);
      }
    }
  }

  private boundW = 0;
  private boundH = 0;
  private boundMat: Uint8Array = new Uint8Array();
  private boundWorld!: World;
  private emitted = 0;

  private light(world: World, cell: number, energy: number) {
    if (this.cells.size >= 7000 || this.cells.has(cell) || world.mat[cell] === 0) return;
    const fuel = 0.45 + Math.random() * 0.45;
    this.cells.set(cell, { fuel, max: fuel, e: Math.max(0, energy), local: true });
    this.heat.delete(cell);
    world.log?.ignite(cell);
  }

  /** A fire another client already started. It stays until their CLEAR arrives. */
  show(cell: number) {
    if (this.cells.has(cell)) return;
    this.cells.set(cell, { fuel: 0.7, max: 0.7, e: 0, local: false });
  }

  step(dt: number, world: World, puff?: (x: number, y: number, kind: "flame" | "soot") => void) {
    this.boundWorld = world;
    this.boundW = world.GW;
    this.boundH = world.GH;
    this.boundMat = world.mat;
    if (!this.cells.size && !this.heat.size) return;
    const cool = Math.exp(-1.8 * dt);
    for (const [cell, value] of this.heat) {
      const left = value * cool;
      if (left < 0.05) this.heat.delete(cell);
      else this.heat.set(cell, left);
    }
    const pending: Array<[number, number]> = [];
    this.emitted = 0;
    for (const [cell, flame] of this.cells) {
      if (!flame.local) continue;
      if (world.mat[cell] === 0) {
        this.cells.delete(cell);
        continue;
      }
      flame.fuel -= dt;
      const x = cell % world.GW;
      const y = Math.floor(cell / world.GW);
      if (puff && this.emitted < 16 && Math.random() < 2.4 * dt) {
        this.emitted++;
        puff((x + 0.5) * CELL, (y + 0.5) * CELL, "flame");
      }
      if (flame.e > 0 && Math.random() < 5.5 * dt) {
        const pick = FIRE_N[Math.random() < 0.35 ? Math.floor(Math.random() * 3) : Math.floor(Math.random() * 8)];
        const nx = x + pick[0];
        const ny = y + pick[1];
        if (nx >= 0 && ny >= 0 && nx < world.GW && ny < world.GH) {
          const ni = ny * world.GW + nx;
          if (world.mat[ni] !== 0 && !this.cells.has(ni)) {
            const hot = (this.heat.get(ni) ?? 0) + 0.45 + Math.random() * 0.4;
            if (hot >= 1) pending.push([ni, flame.e - 1 - (Math.random() < 0.3 ? 1 : 0)]);
            else this.heat.set(ni, hot);
          }
        }
      }
      if (flame.fuel <= 0) {
        this.cells.delete(cell);
        const owner = world.owner[cell] ?? -1;
        world.clearCell(cell);
        if (owner >= 0) this.burned.push(owner);
        for (const [dx, dy] of FIRE_N) {
          const nx = x + dx;
          const ny = y + dy;
          if (nx < 0 || ny < 0 || nx >= world.GW || ny >= world.GH) continue;
          const ni = ny * world.GW + nx;
          if (!this.cells.has(ni) && Math.random() < 0.55) world.charCell(ni, 0.55);
        }
        if (puff && Math.random() < 0.18) puff((x + 0.5) * CELL, (y + 0.5) * CELL, "soot");
      }
    }
    for (const [cell, energy] of pending) this.light(world, cell, energy);
  }

  draw(ctx: CanvasRenderingContext2D, world: World, camX: number, camY: number, scale: number) {
    if (!this.cells.size) return;
    const colors = ["#fff3a6", "#ff8a2a", "#f2561d", "#c9281f"];
    ctx.save();
    for (const [cell, flame] of this.cells) {
      const x = cell % world.GW;
      const y = Math.floor(cell / world.GW);
      const hot = Math.min(1, flame.fuel);
      ctx.globalAlpha = 1;
      ctx.fillStyle = colors[Math.min(3, Math.floor((1 - hot) * 4))];
      const px = (x * CELL - camX) * scale;
      const py = (y * CELL - camY) * scale;
      const size = CELL * scale;
      ctx.fillRect(px, py, size, size);
      ctx.globalAlpha = 0.45 * hot;
      ctx.beginPath();
      ctx.arc(px + size * 0.5, py + size * 0.5, size * 2.4, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }
}
